import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Zap } from 'lucide-react';
export function Hero() {
  return <section className="relative pt-32 pb-16 lg:pt-40 lg:pb-24 overflow-hidden bg-brand-navy" data-id="element-148">
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-149">
        <div className="absolute -top-[20%] -right-[10%] w-[70%] h-[70%] rounded-full bg-brand-blue/10 blur-3xl" data-id="element-150" />
        <div className="absolute top-[40%] -left-[10%] w-[50%] h-[50%] rounded-full bg-brand-green/10 blur-3xl" data-id="element-151" />

        {/* Subtle grid pattern */}
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAwIDEwIEwgNDAgMTAgTSAxMCAwIEwgMTAgNDAiIGZpbGw9Im5vbmUiIHN0cm9rZT0icmdiYSgyNTUsIDI1NSwgMjU1LCAwLjAzKSIgc3Ryb2tlLXdpZHRoPSIxIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2dyaWQpIi8+PC9zdmc+')] opacity-50" data-id="element-152" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-153">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-8 items-center" data-id="element-154">
          {/* Left Content */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="max-w-2xl" data-id="element-155">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6 font-heading" data-id="element-156">
              A Smarter Way to <br data-id="element-157" />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-green to-brand-blue" data-id="element-158">
                Move Faster
              </span>
            </h1>

            <p className="text-lg sm:text-xl text-slate-300 mb-8 leading-relaxed" data-id="element-159">
              AI-powered engineering transformation that combines tools,
              processes, and people to modernize software systems and accelerate
              delivery.
            </p>

            <div className="flex flex-col sm:flex-row gap-4" data-id="element-160">
              <a href="#contact" className="inline-flex justify-center items-center px-6 py-3.5 border border-transparent text-base font-semibold rounded-md text-white bg-brand-green hover:bg-brand-greenHover transition-colors" data-id="element-161">
                Book a Consultation
              </a>
              <a href="#process" className="inline-flex justify-center items-center px-6 py-3.5 border border-slate-600 text-base font-semibold rounded-md text-white hover:bg-slate-800 transition-colors group" data-id="element-162">
                See How It Works
                <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" data-id="element-163" />
              </a>
            </div>
          </motion.div>

          {/* Right Visual */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="relative lg:ml-auto w-full max-w-lg" data-id="element-164">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-slate-800 bg-brand-navyLight" data-id="element-165">
              <div className="flex items-center px-4 py-3 bg-slate-900 border-b border-slate-800" data-id="element-166">
                <div className="flex space-x-2" data-id="element-167">
                  <div className="w-3 h-3 rounded-full bg-red-500/80" data-id="element-168"></div>
                  <div className="w-3 h-3 rounded-full bg-yellow-500/80" data-id="element-169"></div>
                  <div className="w-3 h-3 rounded-full bg-green-500/80" data-id="element-170"></div>
                </div>
                <div className="mx-auto text-xs text-slate-500 font-mono" data-id="element-171">
                  modernize.sh
                </div>
              </div>
              <div className="p-6" data-id="element-172">
                <img src="https://images.unsplash.com/photo-1555066931-4365d14bab8c?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80" alt="Software Code" className="w-full h-auto rounded opacity-80 mix-blend-luminosity hover:mix-blend-normal transition-all duration-500" data-id="element-173" />
              </div>
            </div>

            {/* Floating Badge */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.8,
            duration: 0.5
          }} className="absolute -bottom-6 -left-6 bg-white p-4 rounded-xl shadow-xl border border-slate-100 flex items-center gap-4" data-id="element-174">
              <div className="bg-green-100 p-3 rounded-full text-brand-green" data-id="element-175">
                <Zap size={24} data-id="element-176" />
              </div>
              <div data-id="element-177">
                <p className="text-sm text-slate-500 font-medium" data-id="element-178">
                  Transformation Speed
                </p>
                <p className="text-xl font-bold text-slate-900" data-id="element-179">80% Faster</p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>;
}